import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Sparkles } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  fetchAutoTopupSettings,
  resolveAutoTopupPause,
  runAutoTopupCheck,
} from '../lib/aiTokenAutoTopup';
import toast from 'react-hot-toast';

/**
 * Global interrupt when an AI-token AUTO TOP-UP charge fails: pops wherever
 * the user is so they can retry the payment or keep it paused. Shows only for
 * pausedReason === 'payment_failed' — choosing "Keep paused" acknowledges it
 * ('payment_failed_ack'), which stops the modal until they act from Settings.
 */
export default function AutoTopupFailedModal() {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const businessId = selectedBusiness?.id;

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    // Check shortly after the app settles; re-check when the business changes.
    const timer = setTimeout(async () => {
      const settings = await fetchAutoTopupSettings(businessId);
      if (cancelled || !settings) return;
      if (settings.enabled && settings.pausedReason === 'payment_failed') {
        setLastError(settings.lastError);
        setOpen(true);
      }
    }, 4000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [businessId]);

  if (!open || !businessId) return null;

  const handleRetry = async () => {
    setBusy(true);
    try {
      await resolveAutoTopupPause(businessId, 'retry_clear');
      const result = await runAutoTopupCheck(businessId);
      if (result.toppedUp) {
        toast.success(`Payment went through — topped up ${result.tokensAdded?.toLocaleString()} tokens`);
        setOpen(false);
      } else if (result.paymentFailed) {
        setLastError(result.error || 'The card was declined again.');
        toast.error('Payment failed again. Update your card on file, then retry.');
      } else {
        // Balance recovered above the threshold in the meantime — nothing due.
        toast.success('Auto top-up re-enabled.');
        setOpen(false);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed');
    } finally {
      setBusy(false);
    }
  };

  const handleKeepPaused = async () => {
    setBusy(true);
    try {
      await resolveAutoTopupPause(businessId, 'keep_paused');
    } catch { /* non-critical */ }
    setBusy(false);
    setOpen(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100060] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold text-gray-900">AI token auto top-up failed</h3>
            <p className="text-[13px] text-gray-500 mt-0.5">
              We tried to top up your AI tokens automatically, but the payment didn't go through
              {lastError ? `: ${lastError}` : '.'}
            </p>
          </div>
        </div>

        <div className="px-5 pb-4">
          <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
            <p className="text-[12px] text-gray-600 leading-relaxed">
              Auto top-up is paused until you act — AI features keep working until your remaining balance runs out. You
              can retry the charge now, fix your card in billing, or keep it paused.
            </p>
          </div>
        </div>

        <div className="px-5 pb-5 space-y-2">
          <button
            type="button"
            onClick={handleRetry}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 font-semibold text-white transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
            {busy ? 'Retrying…' : 'Retry payment now'}
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/ai-settings');
            }}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Open AI Settings
          </button>
          <button
            type="button"
            onClick={handleKeepPaused}
            disabled={busy}
            className="w-full px-4 py-2 text-[13px] font-medium text-gray-400 hover:text-gray-600 transition-colors"
          >
            Keep auto top-up paused
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
